import React, { useState, useRef, useEffect } from 'react';
import FilterPopup from './FilterPopup';

function InspectorRoutes({ optimizedRoutes, selectedInspector, setSelectedInspector, generateInspectorColor, selectedDate, setPage, currentUser }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
    const [activeFilters, setActiveFilters] = useState({ 
        departments: [], 
        districts: [],
        taskTypes: [],
        priorities: [],
        schedulingMethods: []
    });
    const searchInputRef = useRef(null);

    /**
     * Creates a text file with inspection information in the format:
     * Record_ID|INSPECTION_ID|INSPECTOR_ID|AppointmentStart|AppointmentFinish
     * 
     * @param {Array} optimizedRoutesWithETAs - Array of optimized routes with ETAs
     */
    const downloadRoutes = (optimizedRoutesWithETAs) => {
        if (!optimizedRoutesWithETAs || optimizedRoutesWithETAs.length === 0) {
            console.warn('No routes to download');
            return;
        }
        
        // Create the content for the text file
        let fileContent = '';
        
        // Process each route
        optimizedRoutesWithETAs.forEach(route => {
            const inspectorId = route.assignedInspector.INSPECTOR_ID;
            
            // Process each inspection in the route
            route.assignedInspections.forEach(inspection => {
                // Format: Record_ID|INSPECTION_ID|INSPECTOR_ID|AppointmentStart|AppointmentFinish
                const row = `${inspection.Record_ID}|${inspection.INSPECTION_ID}|${inspectorId}|${inspection.newAppointmentStart}|${inspection.newAppointmentFinish}`;
                fileContent += row + '\n';
            });
        });
        
        // Create a Blob with the file content
        const blob = new Blob([fileContent], { type: 'text/plain' });
        
        // Create a URL for the Blob
        const url = URL.createObjectURL(blob);
        
        // Create a link element
        const link = document.createElement('a');
        link.href = url;
        
        // Set the filename with the current date
        const date = selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
        link.download = `routes_${date}.txt`;
        
        // Append the link to the document
        document.body.appendChild(link);
        
        // Click the link to download the file
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        console.log('Routes downloaded successfully');
    };

    if (!optimizedRoutes || optimizedRoutes.length === 0) {
        // Format the selected date as "Wednesday, July 30"
        const formatDate = (date) => {
            if (!date) return '';
            const options = { weekday: 'long', month: 'long', day: 'numeric' };
            return date.toLocaleDateString('en-US', options);
        };

        const formattedDate = formatDate(selectedDate);

        return (
            <div className="w-96 h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center max-w-sm mx-auto px-6">
                    <div className="text-gray-500 text-lg mb-2">No inspections scheduled</div>
                    <div className="text-gray-400 text-sm mb-6">
                        {formattedDate && `for ${formattedDate}`}
                    </div>
                    
                    <button
                        onClick={() => {
                            if (currentUser.email === 'testnycdob_read@gmail.com') {
                                alert('You\'re using a read-only account. While you can\'t start new routes, you can explore existing inspection itineraries by browsing the calendar above.');
                                return;
                            }
                            setPage('inspections');
                        }}
                        className="inline-flex items-center px-4 py-2 bg-myteal-500 text-white text-sm font-medium rounded-md hover:bg-myteal-600 transition-colors focus:outline-none focus:ring-2 focus:ring-myteal-500 focus:ring-offset-2"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Start New
                    </button>
                    
                    <div className="text-xs text-gray-400 mt-3">
                        Import inspection data to begin
                    </div>
                </div>
            </div>
        );
    }

    // Filter inspectors based on search term and active filters
    const filteredRoutes = optimizedRoutes.filter(route => {
        const inspector = route.assignedInspector;
        
        // Search filter
        const matchesSearch = inspector.INSPECTOR_ID.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Department filter
        const matchesDepartment = activeFilters.departments.length === 0 || 
            activeFilters.departments.includes(inspector.INSPECTOR_DEPARTMENT);
        
        // District filter
        const matchesDistrict = activeFilters.districts.length === 0 || 
            activeFilters.districts.some(district => 
                inspector.districtCodes && inspector.districtCodes.includes(district)
            );
        
        // Task Type filter - check if inspector has any inspections with selected task types
        const matchesTaskType = activeFilters.taskTypes.length === 0 || 
            activeFilters.taskTypes.some(taskType => 
                route.assignedInspections.some(inspection => inspection.taskType === taskType)
            );
        
        // Priority filter - check if inspector has any inspections with selected priorities
        const matchesPriority = activeFilters.priorities.length === 0 || 
            activeFilters.priorities.some(priority => 
                route.assignedInspections.some(inspection => inspection.priority === priority)
            );
        
        // Scheduling Method filter - check if inspector has any inspections with selected scheduling methods
        const matchesSchedulingMethod = activeFilters.schedulingMethods.length === 0 || 
            activeFilters.schedulingMethods.some(method => 
                route.assignedInspections.some(inspection => inspection.Status === method)
            );
        
        return matchesSearch && matchesDepartment && matchesDistrict && 
               matchesTaskType && matchesPriority && matchesSchedulingMethod;
    });

    // Handle inspector selection/deselection
    const handleInspectorSelect = (inspector) => {
        if (selectedInspector && selectedInspector.INSPECTOR_ID === inspector.INSPECTOR_ID) {
            // If clicking the already selected inspector, deselect it
            setSelectedInspector(null);
        } else {
            // Select the new inspector
            setSelectedInspector(inspector);
        }
        searchInputRef.current?.blur(); // Remove focus from search input
    };

    // Handle filter changes
    const handleFiltersChange = (newFilters) => {
        setActiveFilters(newFilters);
    };

    // Get filter summary for display
    const getFilterSummary = () => {
        const totalFilters = activeFilters.departments.length + 
                           activeFilters.districts.length + 
                           activeFilters.taskTypes.length + 
                           activeFilters.priorities.length + 
                           activeFilters.schedulingMethods.length;
        if (totalFilters === 0) return '';
        return `(${totalFilters} filter${totalFilters !== 1 ? 's' : ''})`;
    };

    // Helper function to get display name for scheduling method
    const getSchedulingMethodDisplayName = (status) => {
        switch (status) {
            case 'Pending': return 'Auto-scheduled';
            case 'Scheduled': return 'Manually scheduled';
            default: return status;
        }
    };

    // Helper function to get display name for task type
    const getTaskTypeDisplayName = (taskType) => {
        switch (taskType) {
            case 'inspection': return 'Inspection';
            case 'reinspection': return 'Re-inspection';
            default: return taskType;
        }
    };

    return (
        <div className="w-96 h-screen bg-white border-r border-gray-200 shadow-lg flex flex-col">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 mt-12 flex-shrink-0">
                <h2 className="text-xl font-semibold text-myblue-900 mb-2">Inspector Routes</h2>
                <p className="text-sm text-gray-600">
                    {filteredRoutes.length} of {optimizedRoutes.length} inspector{filteredRoutes.length !== 1 ? 's' : ''} shown {getFilterSummary()}
                </p>
            </div>

            {/* Search and Filter Input */}
            <div className="pl-6 pr-4 pb-6 border-b border-gray-200 flex-shrink-0">
                <div className="flex space-x-1">
                    <div className="relative flex-1">
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search inspector ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    searchInputRef.current?.focus();
                                }}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    <button
                        onClick={() => setIsFilterPopupOpen(true)}
                        className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                            activeFilters.departments.length > 0 || 
                            activeFilters.districts.length > 0 || 
                            activeFilters.taskTypes.length > 0 || 
                            activeFilters.priorities.length > 0 || 
                            activeFilters.schedulingMethods.length > 0
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                    </button>
                    <button
                        onClick={() => downloadRoutes(optimizedRoutes)}
                        className="px-3 py-1 text-sm border border-myteal-500 text-myteal-500 rounded-md hover:bg-myteal-500 hover:text-white transition-colors"
                        title="Download Routes"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </button>
                </div>

                {/* Active Filter Tags */}
                {(activeFilters.departments.length > 0 || 
                  activeFilters.districts.length > 0 || 
                  activeFilters.taskTypes.length > 0 || 
                  activeFilters.priorities.length > 0 || 
                  activeFilters.schedulingMethods.length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {activeFilters.departments.map(department => (
                            <span
                                key={`dept-${department}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                                {department}
                                <button
                                    onClick={() => handleFiltersChange({
                                        ...activeFilters,
                                        departments: activeFilters.departments.filter(d => d !== department)
                                    })}
                                    className="ml-1 text-blue-600 hover:text-blue-800"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        {activeFilters.districts.map(district => (
                            <span
                                key={`dist-${district}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                            >
                                {district}
                                <button
                                    onClick={() => handleFiltersChange({
                                        ...activeFilters,
                                        districts: activeFilters.districts.filter(d => d !== district)
                                    })}
                                    className="ml-1 text-green-600 hover:text-green-800"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        {activeFilters.taskTypes.map(taskType => (
                            <span
                                key={`task-${taskType}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                            >
                                {getTaskTypeDisplayName(taskType)}
                                <button
                                    onClick={() => handleFiltersChange({
                                        ...activeFilters,
                                        taskTypes: activeFilters.taskTypes.filter(t => t !== taskType)
                                    })}
                                    className="ml-1 text-purple-600 hover:text-purple-800"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        {activeFilters.priorities.map(priority => (
                            <span
                                key={`priority-${priority}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600"
                            >
                                Priority {priority}
                                <button
                                    onClick={() => handleFiltersChange({
                                        ...activeFilters,
                                        priorities: activeFilters.priorities.filter(p => p !== priority)
                                    })}
                                    className="ml-1 text-red-600 hover:text-red-800"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        {activeFilters.schedulingMethods.map(method => (
                            <span
                                key={`scheduling-${method}`}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-600"
                            >
                                {getSchedulingMethodDisplayName(method)}
                                <button
                                    onClick={() => handleFiltersChange({
                                        ...activeFilters,
                                        schedulingMethods: activeFilters.schedulingMethods.filter(m => m !== method)
                                    })}
                                    className="ml-1 text-yellow-600 hover:text-yellow-800"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Inspector Cards */}
            <div className="flex-1 overflow-y-auto">
                {filteredRoutes.map((route, index) => {
                    const inspector = route.assignedInspector;
                    const isSelected = selectedInspector && selectedInspector.INSPECTOR_ID === inspector.INSPECTOR_ID;
                    const stopCount = route.assignedInspections.length;
                    
                    return (
                        <div
                            key={inspector.INSPECTOR_ID}
                            className={`py-4 pr-4 pl-6 border-b border-gray-200 cursor-pointer transition-all duration-200 relative ${
                                isSelected 
                                    ? 'bg-blue-50 border-blue-200' 
                                    : 'hover:bg-gray-50'
                            }`}
                            onClick={() => handleInspectorSelect(inspector)}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    {/* Inspector Name and ID */}
                                    <div className="flex items-center mb-2">
                                        <div 
                                            className="w-3 h-3 rounded-full mr-3" 
                                            style={{ backgroundColor: inspector.assignedColor }}
                                        ></div>
                                        <div>
                                            <div className="font-semibold text-gray-900">
                                                {inspector.INSPECTOR_ID}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {inspector.INSPECTOR_DEPARTMENT}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Route Stats */}
                                    <div className="text-sm text-gray-600 space-y-1">
                                        <div>{stopCount} inspection{stopCount !== 1 ? 's' : ''}</div>
                                        <div>
                                            {route.totalDistance} miles | {route.totalTime} minutes
                                        </div>
                                        {route.breakTime && (
                                            <div className="text-sm text-gray-500">
                                                Break: {new Date(route.breakTime).toLocaleTimeString('en-US', { 
                                                    hour: 'numeric', 
                                                    minute: '2-digit',
                                                    hour12: true 
                                                })}
                                            </div>
                                        )}
                                        <div className="text-sm text-gray-500">
                                            Districts: {inspector.districtCodes?.join(', ') || 'N/A'}
                                        </div>
                                    </div>

                                    {/* Inspector Address */}
                                    <div className="text-xs text-gray-500 mt-2">
                                        {inspector.Street}, {inspector.City}, {inspector.State} {inspector.Postcode}
                                    </div>
                                </div>

                                {/* Selection Indicator */}
                                <div className="ml-4">
                                    <div
                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                            isSelected 
                                                ? 'border-blue-500 bg-blue-500' 
                                                : 'border-gray-300'
                                        }`}
                                    >
                                        {isSelected && (
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Navigate Button - Bottom Right Corner */}
                            {route.curatedLinks?.googleUrl && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent card selection
                                        window.open(route.curatedLinks.googleUrl, '_blank');
                                    }}
                                    className="absolute bottom-4 right-4 p-1.5 bg-white text-blue-600 border border-blue-600 rounded-md hover:bg-blue-600 hover:text-white transition-colors"
                                    title="Open route in Google Maps"
                                >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 512 512"><path d="M272 464a16 16 0 01-16-16.42V264.13a8 8 0 00-8-8H64.41a16.31 16.31 0 01-15.49-10.65 16 16 0 018.41-19.87l384-176.15a16 16 0 0121.22 21.19l-176 384A16 16 0 01272 464z"/></svg>
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Filter Popup */}
            <FilterPopup
                isOpen={isFilterPopupOpen}
                onClose={() => setIsFilterPopupOpen(false)}
                optimizedRoutes={optimizedRoutes}
                activeFilters={activeFilters}
                onFiltersChange={handleFiltersChange}
            />
        </div>
    );
}

export default InspectorRoutes; 